import React, { useState } from 'react';
import api from '../../services/api';

const PrecheckModal = ({ vehicle, onClose, onComplete, onCreateRO, users = [], vendors = [] }) => {
  const [notes, setNotes] = useState(vehicle?.whatItNeeds || '');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Parse photos - handle both array and JSON string formats
  const getPhotos = () => {
    console.log('[PrecheckModal] Vehicle data:', vehicle);
    console.log('[PrecheckModal] whatItNeedsPhotos:', vehicle?.whatItNeedsPhotos);
    console.log('[PrecheckModal] what_it_needs_photos:', vehicle?.what_it_needs_photos);
    const photos = vehicle?.whatItNeedsPhotos || vehicle?.what_it_needs_photos;
    if (!photos) {
      console.log('[PrecheckModal] No photos found');
      return [];
    }
    if (Array.isArray(photos)) {
      console.log('[PrecheckModal] Photos is array with', photos.length, 'items');
      return photos;
    }
    if (typeof photos === 'string') {
      try {
        const parsed = JSON.parse(photos);
        console.log('[PrecheckModal] Parsed JSON photos:', parsed);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        console.log('[PrecheckModal] Failed to parse photos string');
        return [];
      }
    }
    return [];
  };
  const whatItNeedsPhotos = getPhotos();

  const handleCompleteAndCreateRO = async () => {
    setSaving(true);
    try {
      // Prepare precheck data
      const precheckData = {
        vehicleId: vehicle.id,
        originalNotes: notes,
        additionalNotes,
        completedAt: new Date().toISOString()
      };
      
      console.log('=== PRECHECK COMPLETION DEBUG ===');
      console.log('Vehicle ID:', vehicle.id);

      // Prepare the data for the inspection_notes field
      const inspectionNotesPayload = {
        precheck: true,
        notes: additionalNotes,
        originalDamageNotes: notes
      };

      // Update vehicle with precheck data stored in inspection_notes
      // NOTE: Do NOT set transportStatus to 'delivered' - precheck happens BEFORE vehicle arrives
      const updateData = {
        inspectionNotes: JSON.stringify(inspectionNotesPayload),
        damageFlagged: false, // Clear the precheck flag since precheck is complete
        priority: 'normal'
      };
      
      console.log('Updating vehicle with precheck completion:', updateData);
      try {
        await api.updateVehicle(vehicle.id, updateData);
        console.log(' Vehicle updated successfully');
      } catch (updateErr) {
        console.error(' Vehicle update failed:', updateErr);
        throw new Error('Failed to update vehicle: ' + (updateErr.response?.data?.error || updateErr.message));
      }
      
      // Create RO - parts will be added in the RO detail page
      console.log('Creating RO...');
      const roData = {
        assignedToUserId: null,
        priority: 'normal',
        complaint: `Precheck completed: ${additionalNotes || 'See notes'}`
      };
      
      let roResult;
      try {
        roResult = await api.createROFromInspection(vehicle.id, roData);
        console.log(' RO CREATED:', roResult);
      } catch (roErr) {
        console.error(' RO creation failed:', roErr);
        throw new Error('Failed to create RO: ' + (roErr.response?.data?.error || roErr.message));
      }
      
      alert(` Precheck completed and RO #${roResult.roNumber} created!\n\nAdd parts in the RO detail page.`);
      
      // Complete any related dashboard tasks
      try {
        const tasks = await api.getTasks({ vehicleId: vehicle.id, status: 'pending' });
        for (const task of tasks) {
          if (task.title?.toLowerCase().includes('precheck') || 
              task.title?.toLowerCase().includes('inspection') ||
              task.type === 'precheck') {
            await api.updateTask(task.id, { status: 'completed' });
          }
        }
      } catch (taskErr) {
        console.warn('Failed to complete related tasks:', taskErr);
      }

      onComplete(precheckData);
      onClose();
    } catch (err) {
      console.error('Failed to complete precheck and create RO:', err);
      alert('Failed to complete precheck and create RO: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Pre-Check Inspection</h2>
              <p className="text-sm text-gray-600">
                {vehicle.year} {vehicle.make} {vehicle.model} - {vehicle.stockNumber}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Photos Section */}
          {whatItNeedsPhotos.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">📸 Damage Photos ({whatItNeedsPhotos.length})</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {whatItNeedsPhotos.map((photo, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={photo}
                      alt={`Damage ${idx + 1}`}
                      className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-75 transition"
                      onClick={() => window.open(photo, '_blank')}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition rounded flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 text-sm">Click to enlarge</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Original Notes */}
          <div>
            <h3 className="text-lg font-semibold mb-3"> Original Damage Notes</h3>
            <div className="bg-gray-50 p-4 rounded border">
              <p className="text-gray-700">{notes || 'No original notes provided'}</p>
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <h3 className="text-lg font-semibold mb-3">💭 Additional Inspection Notes</h3>
            <textarea
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any additional observations, concerns, or recommendations..."
            />
          </div>

          {/* Info about parts */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-800">
              <span className="text-xl"></span>
              <span className="font-medium">Parts will be added in the RO</span>
            </div>
            <p className="text-sm text-blue-600 mt-1">
              After completing the precheck, an RO will be created where you can add any parts needed.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCompleteAndCreateRO}
              disabled={saving || !additionalNotes.trim()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Processing...' : 'Complete Pre-check & Create RO'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrecheckModal;
